import { useMemo } from 'react';
import { Extension, StateField, StateEffect } from '@codemirror/state';
import { EditorView, showPanel, Panel, keymap } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

type SignatureInfo = {
  name: string;
  params: { name: string; type?: string }[];
  returnType?: string;
  description?: string;
  activeParam: number;
};

// State effect to update signature info
const setSignature = StateEffect.define<SignatureInfo | null>();

// State field to hold current signature
const signatureState = StateField.define<SignatureInfo | null>({
  create: () => null,
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setSignature)) {
        return effect.value;
      }
    }
    // Clear on selection change outside function call
    if (tr.selection && !tr.docChanged) {
      return value; // Keep it on selection changes
    }
    return value;
  },
});

// Extract function signature from JSDoc or declaration
function extractFunctionSignature(code: string, functionName: string): Omit<SignatureInfo, 'activeParam'> | null {
  // Look for function declaration
  const patterns = [
    // function name(params): ReturnType
    new RegExp(`function\\s+${functionName}\\s*\\(([^)]*?)\\)\\s*(?::\\s*([^{]+?))?\\s*\\{`, 's'),
    // const name = (params): ReturnType =>
    new RegExp(`(?:const|let|var)\\s+${functionName}\\s*=\\s*(?:async\\s*)?\\(([^)]*?)\\)\\s*(?::\\s*([^=]+?))?\\s*=>`, 's'),
    // Arrow function with type annotation
    new RegExp(`${functionName}\\s*:\\s*\\(([^)]*?)\\)\\s*=>\\s*([^;,}]+)`, 's'),
    // Object method name(params): ReturnType
    new RegExp(`${functionName}\\s*\\(([^)]*?)\\)\\s*(?::\\s*([^{]+?))?\\s*\\{`, 's'),
  ];

  for (const pattern of patterns) {
    const match = code.match(pattern);
    if (match) {
      const paramsStr = match[1] || '';
      const returnType = match[2]?.trim();

      // Parse parameters
      const params = parseParams(paramsStr);

      return {
        name: functionName,
        params,
        returnType,
      };
    }
  }

  // Check for JSDoc
  const jsDocPattern = new RegExp(
    `/\\*\\*[\\s\\S]*?\\*/\\s*(?:export\\s+)?(?:async\\s+)?(?:function\\s+${functionName}|(?:const|let|var)\\s+${functionName})`,
    'g'
  );
  const jsDocMatch = code.match(jsDocPattern);
  if (jsDocMatch) {
    const jsDoc = jsDocMatch[0];
    const params = extractJsDocParams(jsDoc);
    const returnMatch = jsDoc.match(/@returns?\s+(?:\{([^}]+)\})?\s*(.*)/);

    return {
      name: functionName,
      params: params.length > 0 ? params : [{ name: '...' }],
      returnType: returnMatch?.[1],
      description: extractJsDocDescription(jsDoc),
    };
  }

  return null;
}

// Parse function parameters
function parseParams(paramsStr: string): { name: string; type?: string }[] {
  if (!paramsStr.trim()) return [];

  const params: { name: string; type?: string }[] = [];
  let depth = 0;
  let current = '';

  // Split by comma, respecting nested structures
  for (const char of paramsStr) {
    if (char === '(' || char === '<' || char === '{' || char === '[') {
      depth++;
      current += char;
    } else if (char === ')' || char === '>' || char === '}' || char === ']') {
      depth--;
      current += char;
    } else if (char === ',' && depth === 0) {
      if (current.trim()) {
        params.push(parseParam(current.trim()));
      }
      current = '';
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    params.push(parseParam(current.trim()));
  }

  return params;
}

// Parse a single parameter
function parseParam(param: string): { name: string; type?: string } {
  // Handle destructuring
  if (param.startsWith('{') || param.startsWith('[')) {
    const colonIndex = param.lastIndexOf(':');
    if (colonIndex !== -1) {
      return {
        name: param.slice(0, colonIndex).trim(),
        type: param.slice(colonIndex + 1).trim(),
      };
    }
    return { name: param };
  }

  // Handle name: type = default
  const parts = param.split(':');
  if (parts.length >= 2) {
    const name = parts[0].trim();
    let type = parts.slice(1).join(':').trim();
    // Remove default value
    const eqIndex = type.indexOf('=');
    if (eqIndex !== -1) {
      type = type.slice(0, eqIndex).trim();
    }
    return { name, type };
  }

  // Handle name = default
  const eqIndex = param.indexOf('=');
  if (eqIndex !== -1) {
    return { name: param.slice(0, eqIndex).trim() };
  }

  return { name: param };
}

// Extract params from JSDoc
function extractJsDocParams(jsDoc: string): { name: string; type?: string }[] {
  const params: { name: string; type?: string }[] = [];
  const paramRegex = /@param\s+(?:\{([^}]+)\})?\s*(\w+)/g;
  let match;

  while ((match = paramRegex.exec(jsDoc)) !== null) {
    params.push({
      name: match[2],
      type: match[1],
    });
  }

  return params;
}

// Extract description from JSDoc
function extractJsDocDescription(jsDoc: string): string | undefined {
  const lines = jsDoc.split('\n');
  const description: string[] = [];

  for (const line of lines) {
    const trimmed = line.replace(/^\s*\*?\s*/, '').trim();
    if (trimmed.startsWith('@') || trimmed === '/**' || trimmed === '*/') continue;
    if (trimmed) description.push(trimmed);
  }

  return description.length > 0 ? description.join(' ') : undefined;
}

// Count active parameter by counting commas before cursor
function getActiveParam(code: string, pos: number): number {
  // Find the opening parenthesis
  let depth = 0;
  let start = pos - 1;

  while (start >= 0) {
    const char = code[start];
    if (char === ')' || char === '>' || char === '}' || char === ']') {
      depth++;
    } else if (char === '(' || char === '<' || char === '{' || char === '[') {
      if (depth === 0) break;
      depth--;
    }
    start--;
  }

  if (start < 0) return 0;

  // Count commas between start and pos
  let commas = 0;
  depth = 0;
  for (let i = start + 1; i < pos; i++) {
    const char = code[i];
    if (char === '(' || char === '<' || char === '{' || char === '[') {
      depth++;
    } else if (char === ')' || char === '>' || char === '}' || char === ']') {
      depth--;
    } else if (char === ',' && depth === 0) {
      commas++;
    }
  }

  return commas;
}

// Create signature panel
function createSignaturePanel(view: EditorView): Panel {
  const dom = document.createElement('div');
  dom.className = 'signature-help-panel';

  const update = (signature: SignatureInfo | null) => {
    if (!signature) {
      dom.style.display = 'none';
      return;
    }

    dom.style.display = 'block';
    dom.innerHTML = '';

    // Function name
    const header = document.createElement('div');
    header.className = 'signature-help-header';

    const name = document.createElement('span');
    name.className = 'signature-help-name';
    name.textContent = signature.name;
    header.appendChild(name);

    // Parameters
    const paramsEl = document.createElement('span');
    paramsEl.className = 'signature-help-params';
    paramsEl.textContent = '(';

    signature.params.forEach((param, i) => {
      if (i > 0) {
        paramsEl.appendChild(document.createTextNode(', '));
      }

      const paramEl = document.createElement('span');
      paramEl.className = `signature-help-param ${i === signature.activeParam ? 'active' : ''}`;
      paramEl.textContent = param.name;

      if (param.type) {
        const typeEl = document.createElement('span');
        typeEl.className = 'signature-help-type';
        typeEl.textContent = `: ${param.type}`;
        paramEl.appendChild(typeEl);
      }

      paramsEl.appendChild(paramEl);
    });

    paramsEl.appendChild(document.createTextNode(')'));
    header.appendChild(paramsEl);

    // Return type
    if (signature.returnType) {
      const returnEl = document.createElement('span');
      returnEl.className = 'signature-help-return';
      returnEl.textContent = `: ${signature.returnType}`;
      header.appendChild(returnEl);
    }

    dom.appendChild(header);

    // Description
    if (signature.description) {
      const desc = document.createElement('div');
      desc.className = 'signature-help-description';
      desc.textContent = signature.description;
      dom.appendChild(desc);
    }
  };

  return {
    dom,
    update(viewUpdate) {
      const signature = viewUpdate.state.field(signatureState);
      update(signature);
    },
  };
}

// Update signature on cursor position change
function updateSignature(view: EditorView) {
  const pos = view.state.selection.main.head;
  const code = view.state.doc.toString();

  // Check if we're inside a function call
  const tree = syntaxTree(view.state);
  let node = tree.resolveInner(pos, -1);

  // Walk up to find function call
  let functionName: string | null = null;
  let searchNode = node;

  while (searchNode) {
    const nodeName = searchNode.name;

    // Look for CallExpression or similar
    if (nodeName === 'CallExpression' || nodeName === 'Arguments') {
      // Find the function name
      const callNode = searchNode.parent;
      if (callNode) {
        const firstChild = callNode.firstChild;
        if (firstChild) {
          functionName = code.slice(firstChild.from, firstChild.to);
          // Handle method calls like obj.method
          if (functionName.includes('.')) {
            functionName = functionName.split('.').pop() || null;
          }
        }
      }
      break;
    }

    if (nodeName === 'ArgList' || nodeName === 'ParamList') {
      // Go up to find the function
      let parent = searchNode.parent;
      while (parent) {
        if (parent.name === 'CallExpression') {
          const callee = parent.firstChild;
          if (callee) {
            functionName = code.slice(callee.from, callee.to);
            if (functionName.includes('.')) {
              functionName = functionName.split('.').pop() || null;
            }
          }
          break;
        }
        parent = parent.parent;
      }
      break;
    }

    searchNode = searchNode.parent!;
  }

  if (!functionName) {
    // Try simple heuristic: look for function( before cursor
    let depth = 0;
    let i = pos - 1;
    while (i >= 0) {
      const char = code[i];
      if (char === ')') depth++;
      else if (char === '(') {
        if (depth === 0) {
          // Found opening paren, get function name
          let nameEnd = i;
          let nameStart = nameEnd - 1;
          while (nameStart >= 0 && /[\w$]/.test(code[nameStart])) {
            nameStart--;
          }
          nameStart++;
          if (nameStart < nameEnd) {
            functionName = code.slice(nameStart, nameEnd);
          }
          break;
        }
        depth--;
      }
      i--;
    }
  }

  if (!functionName) {
    view.dispatch({ effects: setSignature.of(null) });
    return;
  }

  // Get signature info
  const signature = extractFunctionSignature(code, functionName);
  if (!signature) {
    view.dispatch({ effects: setSignature.of(null) });
    return;
  }

  // Get active parameter
  const activeParam = getActiveParam(code, pos);

  view.dispatch({
    effects: setSignature.of({ ...signature, activeParam }),
  });
}

export function useSignatureHelp() {
  const signatureHelpExtension = useMemo<Extension>(() => {
    return [
      signatureState,
      showPanel.of((view) => createSignaturePanel(view)),
      EditorView.updateListener.of((update) => {
        if (update.selectionSet || update.docChanged) {
          updateSignature(update.view);
        }
      }),
      keymap.of([
        {
          // Cmd+Shift+Space to trigger signature help
          key: 'Mod-Shift-Space',
          run: (view) => {
            updateSignature(view);
            return true;
          },
        },
        {
          // Escape to hide
          key: 'Escape',
          run: (view) => {
            const current = view.state.field(signatureState);
            if (current) {
              view.dispatch({ effects: setSignature.of(null) });
              return true;
            }
            return false;
          },
        },
      ]),
    ];
  }, []);

  return { signatureHelpExtension };
}
